import { useMedication, useProducts } from '@hook'
import { statusType } from '@type/medication.type'
import { FormButton, Loader, Modal, RadioBtn } from '@ui'
import { getError, showToast } from '@util'
import { Fragment, useMemo, useState } from 'react'

type Props = {
  onClose: () => void
  id?: string
  drugName?: string
}

export type CardProps = {
  handleChecked: (arg: string) => void
  id: string
  items: string[]
}

export const Card = ({ id, items, handleChecked }: CardProps) => (
  <label className={`flex items-center px-4 gap-2 py-3 cursor-pointer transition-all`}>
    <RadioBtn
      name="code"
      onChange={() => {
        handleChecked(id)
      }}
      type="radio"
    />
    <div className="flex flex-wrap">
      {items?.map((item, index) => (
        <Fragment key={index}>
          <span className="whitespace-nowrap">{item}</span>
          {index !== items?.length - 1 && <span aria-hidden="true" className="mx-2 h-4 w-px bg-gray-300" />}
        </Fragment>
      ))}
    </div>
  </label>
)

export const VerifyMedication = ({ onClose, drugName, id }: Props) => {
  const [loading, setLoading] = useState(false)
  const { updateMedicationStatus } = useMedication()
  const [checked, handleChecked] = useState('')
  const { products, included, productsIsLoading } = useProducts({ query: drugName })

  const stockedProducts = useMemo(() => (products || []).filter(p => Number(p.attributes?.total_stock) > 0), [products])
  const zeroStockOnly = Boolean(products?.length) && stockedProducts.length === 0
  const canMarkUnavailable = !products?.length || zeroStockOnly

  const handleStatusUpdate = async () => {
    setLoading(true)
    const verified = {
      status: statusType.verified,
      product_id: checked
    }
    const unavailable = {
      status: statusType.not_available
    }
    try {
      if (checked) {
        await updateMedicationStatus({ id: id || '', payload: verified })
        showToast('Drug verified successfully')
      } else if (canMarkUnavailable) {
        await updateMedicationStatus({ id: id || '', payload: unavailable })
        showToast('Drug marked as unavailable')
      }
      onClose()
    } catch (error) {
      getError(error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal onClose={onClose} title="Verify Medication">
      <div className="flex flex-col overflow-auto w-full max-w-[600px]">
        <div className="flex flex-col overflow-auto p-6 gap-4 divide-y divide-gray-700">
          {productsIsLoading ? (
            <Loader />
          ) : stockedProducts.length > 0 ? (
            stockedProducts.map(
              ({
                id: productId,
                attributes: { brand_name, generic_name, total_stock, dosage_form },
                relationships
              }) => {
                const expireId = relationships.nearest_expiring_inventory.data?.id
                const expireDate = included?.find(
                  ({ type, id: invId }) => type === 'product_inventory' && expireId === invId
                )?.attributes.expiry_date
                return (
                  <Card
                    handleChecked={handleChecked}
                    id={productId}
                    items={[
                      `${dosage_form} ${generic_name}`,
                      brand_name,
                      `${total_stock}`,
                      ...(expireDate ? [expireDate] : [])
                    ]}
                    key={productId}
                  />
                )
              }
            )
          ) : (
            <div className="h-64 flex justify-center items-center flex-col gap-4">
              <p className="text-xl font-medium">
                {zeroStockOnly ? `0 stock for ${drugName}` : `0 results for ${drugName}`}
              </p>
              <p className="text-sm">
                {zeroStockOnly
                  ? 'This drug is in inventory but stock is 0. You can mark it as unavailable.'
                  : 'This drug is not available in the inventory'}
              </p>
            </div>
          )}
        </div>
        <FormButton
          disabled={stockedProducts.length > 0 && !checked}
          isLoading={loading}
          onClick={handleStatusUpdate}
          onClose={onClose}
          submit={canMarkUnavailable ? 'Mark as Unavailable' : 'Verify Drug'}
        />
      </div>
    </Modal>
  )
}
